import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import { motion } from "framer-motion";
import InsuranceHomeComponent from "./InsuranceHomeComponent";
import {
  FaHeartbeat,
  FaHospitalAlt,
  FaProcedures,
  FaAmbulance,
  FaBaby,
  FaHeartbeat as FaIllness,
  FaUserShield,
  FaLock,
  FaGavel,
  FaFire,
  FaBomb,
  FaPlaneSlash,
  FaSuitcase,
  FaTooth,
  FaCarCrash,
  FaHome,
  FaReceipt,
} from "react-icons/fa";
import {
  FaUserMd,
  FaBalanceScale,
  FaChartLine,
  FaCar,
  FaBuilding,
} from "react-icons/fa";
import {
  ShieldCheck,
  Star,
  LayoutList,
  FileText,
  Calculator,
  Users,
} from "lucide-react";
import { AnimatePresence } from "framer-motion";
import {
  Wallet,
  BarChart3,
  ScrollText,
  Hourglass,
  RefreshCw,
  Hospital,
  PlusCircle,
} from "lucide-react";

const InsuranceNewUiHome = () => {
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
    {
      question: "  What is insurance?",
      answer:
        "Insurance is a financial arrangement where individuals or organisations pay premiums to an insurance provider in exchange for protection against potential financial losses or risks. It serves as a risk management tool to mitigate the impact of unexpected events.",
    },
    {
      question: "How does insurance work?",
      answer:
        "When you purchase insurance, you agree to pay regular premiums to the insurance company. In return, the insurer provides financial coverage for specified risks, such as property damage, illness, or death, depending on the policy. If a covered event occurs, the insurer compensates you according to the policy terms.",
    },
    {
      question: "   What types of insurance are available?",
      answer:
        " Common types of insurance include health insurance, auto insurance, life insurance, homeowners insurance, and disability insurance. Each type provides coverage for specific risks and circumstances.",
    },
    {
      question: "Why do I need insurance? ",
      answer:
        "  Insurance helps manage financial risks by providing a safety net against unforeseen events. It reduces the financial burden of emergencies, such as medical expenses, property damage, or loss of income, and offers peace of mind.",
    },
    {
      question:
        " What is the difference between term and whole life insurance? ",
      answer:
        "Term life insurance provides coverage for a specific period, such as 10 or 20 years, and pays out a benefit only if the insured person dies within that term. Whole life insurance offers coverage for the insured entire lifetime and includes a savings component that builds cash value over time.",
    },
    {
      question: "Can I change my insurance policy?",
      answer:
        " Yes, you can often modify your insurance policy to adjust coverage levels, add or remove beneficiaries, or update personal information. Contact your insurance provider to discuss any changes you wish to make.",
    },
    {
      question: "What should I consider when choosing an insurance policy?",
      answer:
        " When selecting an insurance policy, consider factors such as the coverage options, premium costs, deductibles, exclusions, and the insurer reputation. Ensure the policy meets your specific needs and financial situation.",
    },
    {
      question: "How do I file an insurance claim?",
      answer:
        "To file a claim, contact your insurance provider and provide details about the event or loss. You will need to complete a claim form and submit any required documentation. The insurer will review your claim and determine the amount of compensation based on the policy terms.",
    },

    {
      question:
        "What is the difference between premium and deductible in insurance?",
      answer:
        " A premium is the amount you regularly pay to keep your insurance policy active. A deductible is the portion of the claim amount you must pay out of pocket before the insurer covers the rest. Both are essential in determining the cost-effectiveness of your policy.",
    },

    {
      question: "How are insurance premiums calculated?",
      answer:
        " Insurance premiums are based on multiple factors like age, health condition, lifestyle, type of coverage, and risk level. For example, a young and healthy individual might pay less for health insurance compared to someone older or with a medical history.",
    },
    {
      question: "What is a beneficiary in life insurance?",
      answer:
        " A beneficiary is the individual or entity you designate to receive the insurance payout in case of your death. You can name more than one beneficiary and even assign percentages to divide the claim amount as per your wishes.",
    },
    {
      question: "Can I have more than one insurance policy at a time?",
      answer:
        " Yes, you can hold multiple insurance policies simultaneously for added protection. Many individuals opt for separate health, life, motor, and home insurance policies to cover different aspects of their lives comprehensively.",
    },
    {
      question: "What is a grace period in insurance?",
      answer:
        " A grace period is the extra time given after the premium due date during which you can still pay the premium without policy lapse. It usually ranges from 15 to 30 days, depending on the insurer and policy type.",
    },

    {
      question: "Is insurance valid internationally?",
      answer:
        " Some insurance policies, especially travel and health plans, may offer international coverage. However, this depends on the terms of the policy, and you may need to buy global coverage or riders for extended international protection.",
    },

    {
      question: "What is co-pay in health insurance",
      answer:
        "Co-pay is the fixed percentage of the claim amount that you must pay during a hospital bill settlement. For example, if your policy has a 10% co-pay clause and your bill is ₹50,000, you pay ₹5,000, and the insurer pays the rest.",
    },
    {
      question: "Can insurance cover be increased later?",
      answer:
        " Yes, many insurance providers allow you to increase your coverage during policy renewal or after major life events like marriage, childbirth, or a rise in income. This often involves re-assessment and possible premium adjustments.",
    },
    {
      question: "Do all insurance claims get approved?",
      answer:
        " Not always. Claims can be denied if the policyholder provides false information, submits incomplete documents, or if the claim falls outside the coverage scope. Reading the terms and conditions thoroughly helps avoid such issues.",
    },
    {
      question: "What is the claim settlement ratio of an insurer?",
      answer:
        " The claim settlement ratio (CSR) is the percentage of claims an insurer has settled out of the total claims received in a year. A high CSR indicates a reliable insurance company and improves your chances of a smooth claim process.",
    },
  ];

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  const cardDataS = [
    {
      imageUrl: "/assets/newacko.png",
      type: "Car Insurance",
      title: "Acko General Insurance",
      premiumStarting: "@ ₹2,094",
      claimSettlement: "98%",
      networkGarages: "4000+",
      learnMoreLink: "/car_insurance/acko-general-insurance",
      applyNowLink: "https://www.acko.com/car-insurance/",
    },
    {
      imageUrl: "/assets/icicilombard.jpg",
      title: "ICICI Lombard General Insurance",
      premiumStarting: "₹610",
      claimSettlement: "100%",
      networkGarages: "10,200+",
      learnMoreLink: "/bike_insurance/icici-lombard-general-insurance",
      applyNowLink: "https://www.icicilombard.com/health-insurance",
      type: "Bike Insurance",
    },
    {
      imageUrl: "/assets/tataaij.jpg",
      title: "Tata AIG General Insurance",
      premiumStarting: "₹40.82",
      claimSettlement: "98%",
      networkGarages: "4000+",
      learnMoreLink: "/travel_insurance/tata-aig-general-insurance",
      applyNowLink: "https://www.acko.com/car-insurance/",
      type: "Travel Insurance",
    },
    {
      imageUrl: "/assets/newsbi.jpg",
      title: "SBI General Insurance",
      premiumStarting: "₹750",
      claimSettlement: "98%",
      networkGarages: "16,625+",
      learnMoreLink: "/health_insurance/sbi-general-insurance",
      applyNowLink: "http://sbigeneral.in/health-insurance",
      type: "Health Insurance",
    },
    {
      imageUrl: "/assets/axisnew.png",
      title: "Axis Max Life Insurance",
      premiumStarting: "₹527/Month",
      claimSettlement: "99.65%",
      networkGarages: "2Cr+",
      learnMoreLink: "/termlife_insurance/axis-max-life-insurance",
      applyNowLink: "https://www.axismaxlife.com/",
      type: "Term Life Insurance",
    },
    //   {
    //     imageUrl: '/assets/hdfcnew.png',
    //     title: 'HDFC ERGO General Insurance',
    //     premiumStarting: '₹31',
    //     claimSettlement: '$1000%',
    //     networkGarages:"$1000000+",
    //     learnMoreLink: '/car_insurance/hdfc-ergo-general-insurance',
    //     applyNowLink: 'https://www.hdfcergo.com/campaigns/buy-travel-insurance-new?utm_source=google_search_1&utm_medium=cpc&utm_campaign=Travel_Search_Brand_Neev-Broad-1&utm_adgroup=Generic&adid=607777551331&utm_term=hdfc%20ergo%20travel%20insurance&utm_network=g&utm_matchtype=e&utm_device=c&utm_location=1007765&utm_sitelink={sitelink}&utm_placement=&ci=googlesearch&SEM=1&gad_source=1&gclid=CjwKCAjw5PK_BhBBEiwAL7GTPfVMj58IXix3iQqI1jYE9rHublRlFThUgiOU-WtymVmbDVt2kmLSGBoCcaMQAvD_BwE',
    //     type: 'Car Insurance',
    //   },

    // Add more card data objects here for additional cards
  ];
  const renderCardDetails = (card) => {
    if (card.type === "Car Insurance") {
      return (
        <>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Premium Starting:</strong> {card.premiumStarting}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Claim Settlement:</strong> {card.claimSettlement}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Network Garages:</strong> {card.networkGarages}
          </p>
        </>
      );
    } else if (card.type === "Bike Insurance") {
      return (
        <>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Premium Starting:</strong> {card.premiumStarting}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Claim Settlement:</strong> {card.claimSettlement}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Network Garages:</strong> {card.networkGarages}
          </p>
        </>
      );
    } else if (card.type === "Travel Insurance") {
      return (
        <>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Premium Starting:</strong> {card.premiumStarting}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Baggage Loss:</strong> {card.claimSettlement}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Medical Expenses:</strong> {card.networkGarages}
          </p>
        </>
      );
    } else if (card.type === "Health Insurance") {
      return (
        <>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Premium Starting:</strong> {card.premiumStarting}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Claim Settlement:</strong> {card.claimSettlement}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Hospital Networks:</strong> {card.networkGarages}
          </p>
        </>
      );
    } else if (card.type === "Term Life Insurance") {
      return (
        <>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Premium Starting:</strong> {card.premiumStarting}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Claim Settlement:</strong> {card.claimSettlement}
          </p>
          <p className="text-gray-600 text-base text-center mt-2">
            <strong>Life Cover:</strong> {card.networkGarages}
          </p>
        </>
      );
      // } else if (card.type === 'Car Insurance') {
      //     return (
      //       <>
      //         <p className="text-gray-600 text-base text-center mt-2"><strong>Premium Starting:</strong> {card.premiumStarting}</p>
      //         <p className="text-gray-600 text-base text-center mt-2"><strong>Claim Settlement</strong> {card.claimSettlement}</p>
      //         <p className="text-gray-600 text-base text-center mt-2"><strong>Network Garages</strong> {card.networkGarages}</p>
      //       </>
      //     );
    } else {
      return null;
    }
  };
  const insuranceCoverage = [
    {
      title: "Accidental Death and Disability",
      icon: <FaUserShield className="text-2xl text-red-600" />,
      description:
        "Many insurance plans—especially life, personal accident, and term policies—cover death or permanent/partial disability due to an accident. This ensures that your family receives compensation or a payout to sustain their financial future even if you’re no longer able to earn.",
    },
    {
      title: "Hospitalization (Pre and Post)",
      icon: <FaHospitalAlt className="text-2xl text-indigo-600" />,
      description:
        "Health insurance plans typically cover expenses incurred both before admission (diagnostics, consultation, checkups) and after discharge (medications, follow-ups), ensuring complete financial support during treatment.",
    },
    {
      title: "Daycare Procedures",
      icon: <FaProcedures className="text-2xl text-green-600" />,
      description:
        "Modern insurance policies include treatments that don’t require 24-hour hospitalization—such as cataract surgery, chemotherapy, or tonsillectomy—acknowledging the advancements in medical technology.",
    },
    {
      title: "Ambulance Charges",
      icon: <FaAmbulance className="text-2xl text-orange-500" />,
      description:
        "Most health insurance plans include the cost of ambulance transportation, whether it's for an emergency hospital admission or inter-hospital transfer, helping policyholders avoid additional stress during critical times.",
    },
    {
      title: "Maternity and Newborn Coverage",
      icon: <FaBaby className="text-2xl text-pink-500" />,
      description:
        "Specialized or comprehensive health plans offer maternity benefits including delivery expenses (normal or cesarean), prenatal/postnatal care, and even coverage for the newborn baby for a limited time post-delivery.",
    },
    {
      title: "Critical Illness Coverage",
      icon: <FaIllness className="text-2xl text-purple-600" />,
      description:
        "Plans designed for critical illnesses provide a lump-sum payout upon diagnosis of major conditions such as cancer, stroke, heart attack, or organ failure. This amount can be used for treatment or daily living expenses.",
    },
    {
      title: "Personal Accident Cover",
      icon: <FaUserShield className="text-2xl text-yellow-600" />,
      description:
        "This add-on or standalone policy compensates for accidental injuries, dismemberment, or death, helping individuals or families manage the sudden impact of life-altering events.",
    },
    {
      title: "Theft or Burglary",
      icon: <FaLock className="text-2xl text-gray-600" />,
      description:
        "Home, motor, and travel insurance plans may offer coverage for loss or damage due to burglary or theft, including stolen household items, vehicle accessories, or baggage while travelling.",
    },
    {
      title: "Third-Party Legal Liabilities",
      icon: <FaGavel className="text-2xl text-red-700" />,
      description:
        "Motor insurance and some personal insurance policies cover legal liability arising from causing injury or property damage to a third party—helping you manage lawsuits or compensation claims without financial strain.",
    },
    {
      title: "Natural Calamities (Earthquake, Floods, Fire)",
      icon: <FaFire className="text-2xl text-orange-600" />,
      description:
        "Insurance can protect against damage caused by natural disasters like floods, earthquakes, or fires. This applies to home insurance, travel insurance, and comprehensive vehicle insurance policies.",
    },
    {
      title: "Man-made Events (Riots, Strikes)",
      icon: <FaBomb className="text-2xl text-black" />,
      description:
        "Many property and motor insurance policies include coverage for damages arising from man-made disruptions such as riots, terrorism, arson, or civil unrest—protecting your assets against unpredictable chaos.",
    },
    {
      title: "Trip Cancellations and Delays",
      icon: <FaPlaneSlash className="text-2xl text-sky-600" />,
      description:
        "Travel insurance plans cover losses arising from flight cancellations, delays, or curtailments due to unforeseen events like medical emergencies or political unrest, reimbursing you for non-refundable expenses.",
    },
    {
      title: "Lost Baggage and Passports",
      icon: <FaSuitcase className="text-2xl text-lime-600" />,
      description:
        "One of the most valued features of travel insurance, this ensures reimbursement for lost or delayed baggage and assists with recovery or compensation if passports and essential documents go missing during your journey.",
    },
    {
      title: "OPD and Dental Treatments (in Some Plans)",
      icon: <FaTooth className="text-2xl text-teal-500" />,
      description:
        "Comprehensive and top-tier health insurance plans may cover outpatient consultations, diagnostics, and even dental procedures, which are often excluded from basic health policies.",
    },
    {
      title: "Roadside Assistance (Car/Bike Insurance)",
      icon: <FaCarCrash className="text-2xl text-amber-600" />,
      description:
        "Motor insurance add-ons include 24/7 roadside assistance for breakdowns, flat tires, towing, battery issues, and minor mechanical repairs—ensuring safety and convenience for drivers on the road.",
    },
    {
      title: "Home and Content Insurance",
      icon: <FaHome className="text-2xl text-blue-800" />,
      description:
        "These plans protect not just the physical structure of your home but also the contents inside, such as furniture, electronics, appliances, and even jewelry, from damage, theft, or disasters.",
    },
    {
      title: "Tax-Saving Benefits (Select Insurance Types)",
      icon: <FaReceipt className="text-2xl text-green-700" />,
      description:
        "Health, life, and term insurance premiums qualify for tax deductions under Sections 80C, 80D, and 10(10D)—helping you save on taxes while securing your future.",
    },
  ];

  const factors = [
    {
      title: "Coverage Amount (Sum Insured)",
      icon: <ShieldCheck className="w-6 h-6 text-blue-400" />,
      content: `The most fundamental aspect of any insurance policy is the coverage amount, or the sum insured. This is the maximum amount your insurer will pay in case of a claim. You must ensure that this amount is in line with your income, lifestyle expenses, current debts, and future financial obligations like children’s education, medical inflation, or home loans. For example, a health insurance policy for a family of four in a metro city should ideally have a coverage of ₹10–15 lakhs or more due to the rising cost of hospitalization. Similarly, your life insurance should ideally be 10-15 times your annual income. Inadequate coverage defeats the purpose of buying insurance in the first place.`,
    },
    {
      title: "Premium Affordability",
      icon: <Wallet className="w-6 h-6 text-green-400" />,
      content: `While it’s important to get sufficient coverage, it shouldn’t come at the cost of straining your monthly budget. A policy with high premiums may offer exhaustive benefits but could become unsustainable over time. Always strike a balance between affordability and inclusivity. Look for insurers who offer customizable plans or allow monthly, quarterly, or annual payments to suit your financial convenience. Use online premium calculators to compare plans before settling on one.`,
    },
    {
      title: "Claim Settlement Ratio (CSR)",
      icon: <BarChart3 className="w-6 h-6 text-yellow-400" />,
      content: `This metric indicates the number of claims an insurer has successfully settled against the number received. A high CSR—preferably above 95%—reflects the insurer's reliability and commitment to fulfilling claims. Always check the CSR of the insurer for the past few years, not just one, as consistency is key. This data is publicly available on the IRDAI website. A low CSR could result in delays, disputes, or outright rejection of claims when you need help the most.`,
    },
    {
      title: "Policy Inclusions and Exclusions",
      icon: <ScrollText className="w-6 h-6 text-pink-400" />,
      content: `This is where you need to go beyond just brochures and marketing. Every policy has a set of conditions it will cover (inclusions) and a list it won’t (exclusions). These might include pre-existing diseases, certain types of accidents, cosmetic procedures, or complications arising from substance abuse. Knowing these details will help you make informed decisions and avoid last-minute surprises during claims. Always read the policy document thoroughly, and when in doubt, speak to a representative or advisor.`,
    },
    {
      title: "Waiting Periods",
      icon: <Hourglass className="w-6 h-6 text-red-400" />,
      content: `Most health, maternity, and critical illness insurance policies come with a waiting period ranging from 1 to 4 years before you can claim for pre-existing diseases or specific treatments like cataract, hernia, or maternity. If you’re purchasing insurance for immediate use or already have a condition, this factor becomes crucial. Policies with shorter waiting periods are preferable, especially for those with health concerns or older applicants. Always check if any disease or benefit has a condition-specific waiting clause.`,
    },
    {
      title: "Renewability of the Policy",
      icon: <RefreshCw className="w-6 h-6 text-indigo-400" />,
      content: `Insurance is often bought with long-term needs in mind, so the policy’s renewability status is a major deciding factor. Especially in health and term insurance, ensure the policy offers lifetime renewability. This means you won’t be denied coverage later in life when the need for it increases. Policies that allow renewal only up to a certain age (like 60 or 65) can leave you vulnerable in your senior years. Opting for a plan with lifelong renewability secures your future and reduces the hassle of reapplying or medical reassessments.`,
    },
    {
      title: "Network of Cashless Hospitals or Garages",
      icon: <Hospital className="w-6 h-6 text-orange-400" />,
      content: `For health and motor insurance, the availability of a vast and reputed cashless network plays a key role in reducing out-of-pocket expenses. In case of hospitalization or car repair, being able to access a wide network of partner hospitals or garages means you won’t have to run around for reimbursements. Check if the hospitals near your home, office, or frequently visited cities are covered. The wider the network, the smoother your claim process will be, especially during emergencies.`,
    },
    {
      title: "Add-on Riders and Benefits",
      icon: <PlusCircle className="w-6 h-6 text-teal-400" />,
      content: `Riders are optional add-ons that enhance the coverage of your base policy. They provide protection against specific risks or situations, such as:
      - Accidental Death Benefit: Offers additional payout if the insured dies in an accident.
      - Critical Illness Rider: Pays a lump sum on diagnosis of life-threatening illnesses like cancer or heart attack.
      - Maternity and Newborn Coverage: Helps young couples manage childbirth-related expenses.
      - Room Rent Waiver: Removes limits on room rent during hospitalization.
      - Roadside Assistance: For immediate help in case of car breakdowns.
      - Zero Depreciation Cover: In motor insurance, ensures full claim value without depreciation deductions.`,
    },
  ];
  const factors2 = [
    {
      title: "1. 🧓 Age of the Insured",
      content: `Age is one of the most influential factors when it comes to insurance. Younger individuals are statistically healthier and less prone to critical illnesses or death, which makes them less risky for insurers. Hence, premiums are significantly lower if you buy health or life insurance at a younger age. For example, a 25-year-old can get a term insurance plan with double the coverage at half the cost compared to someone aged 40. Similarly, senior citizens face higher premiums and limited plan availability due to higher health risks.`,
    },
    {
      title: "2. 🩺 Medical History",
      content: `Your past and present medical conditions play a critical role in insurance pricing. If you have a history of chronic illnesses like diabetes, hypertension, or cancer, insurers may:
    - Charge higher premiums
    - Impose waiting periods
    - Exclude certain treatments
    - Require additional medical check-ups
    Even your family’s medical history is sometimes taken into account, especially in life and critical illness plans. Transparency in medical disclosures is essential to avoid claim rejections later.`,
    },
    {
      title: "3. 🚬 Lifestyle Choices",
      content: `Unhealthy habits significantly affect insurance terms. Individuals who smoke, drink heavily, or lead a sedentary lifestyle are considered high-risk due to the associated health issues. Life and health insurers often place such policyholders in higher-risk categories, leading to:
    - Increased premiums
    - Exclusion of certain diseases
    - Need for frequent check-ups
    Insurance companies also assess your BMI (Body Mass Index) to understand your fitness level. Maintaining a healthy lifestyle not only ensures better coverage but also opens up wellness benefits in select policies.`,
    },
    {
      title: "4. 🌍 Geographical Location",
      content: `Where you live can influence your insurance premiums, especially for motor, health, and home insurance:
    - Health insurance: Metro cities have higher hospitalization costs, leading to higher premiums.
    - Motor insurance: Urban areas with more traffic and accident rates see increased motor insurance rates.
    - Home insurance: Areas prone to natural disasters, theft, or high crime have more expensive home coverages.
    Your location also impacts access to cashless hospital networks or garages, which can influence your insurer’s offerings.`,
    },
    {
      title: "5. 🚗 Type of Vehicle or Property",
      content: `In the case of motor or home insurance, the kind of asset you own plays a big role:
    - Luxury or sports cars attract higher premiums due to costly repairs and theft risks.
    - Old or poorly maintained vehicles might be denied comprehensive coverage or given a limited plan.
    - High-value properties or homes located in risk-prone areas (like flood zones) increase the cost of home insurance.
    Add-ons like zero depreciation, theft protection, and engine cover further affect the total premium payable.`,
    },
    {
      title: "6. 👷 Occupation",
      content: `Your profession can impact life and health insurance costs. Jobs involving physical risk, mental stress, or frequent travel—such as pilots, miners, police officers, construction workers, and offshore oil workers—are considered high-risk:
    - Higher premiums apply
    - Limited availability of certain covers
    - Special riders or exclusions may be imposed
    Even self-employed individuals might be assessed differently based on income regularity and work nature.`,
    },
    {
      title: "7.  📋 Claim History",
      content: `If you’ve made frequent claims in the past—especially in motor or health insurance—insurers perceive you as high-risk and may:
    - Raise your renewal premium
    - Reduce or eliminate your No Claim Bonus (NCB)
    - Impose conditions on future claims
    Maintaining a clean or minimal claim record not only reduces premiums over time but also gives access to loyalty benefits.`,
    },
    {
      title: "8.  📆 Policy Duration and Premium Payment Mode",
      content: `Policy tenure and payment frequency matter. Generally, longer-term policies (like 3 or 5 years) offer better pricing than annual renewals. Also:
    - Yearly or one-time premium payment modes attract discounts compared to monthly or quarterly options.
    - Single-premium policies in life insurance can be more cost-effective in the long run.
    Insurers reward long-term commitment with better terms, so choose your policy duration wisely.`,
    },
  ];

  const insurancePoints = [
    {
      icon: <LayoutList className="w-6 h-6 text-blue-600" />,
      title: "Curated Comparisons",
      content: `We bring together top-rated insurance plans from multiple providers in one place. Our curated comparison tables let you see side-by-side differences in premiums, inclusions, claim processes, and additional benefits. No jargon, no bias—just clear facts to help you compare efficiently.`,
    },
    {
      icon: <FileText className="w-6 h-6 text-purple-600" />,
      title: "Updated Guides and Blogs",
      content: `Our knowledge base is consistently updated with practical, easy-to-understand guides, articles, and explainers. From “how to choose the right sum insured” to “understanding policy exclusions,” we ensure you’re informed at every step. We break down legal jargon into real-world language so anyone can understand.`,
    },
    {
      icon: <Star className="w-6 h-6 text-yellow-500" />,
      title: "Expert Reviews and Recommendations",
      content: `Our team of certified insurance analysts regularly reviews new and existing policies. Each plan is rated based on critical parameters like:
      - Claim settlement ratio
      - Network strength
      - Customer support
      - Inclusion versatility
      - Real user experiences
      These expert insights ensure you’re not misled by shiny advertising and actually choose the right fit for your situation.`,
    },
    {
      icon: <FileText className="w-6 h-6 text-green-600" />,
      title: "Category-Specific Pages",
      content: `FinMaster offers dedicated landing pages for each type of insurance—car, bike, term life, health, travel, and more. Each page includes:
      - Detailed guides
      - FAQs
      - Plan comparison
      - Benefits of each category
      - Step-by-step application help
      It’s like having a personal advisor built into the platform.`,
    },

    {
      icon: <Calculator className="w-6 h-6 text-red-600" />,
      title: "Insurance Calculators and Tools",
      content: `FinMaster offers a suite of advanced tools to simplify your insurance decisions. Our interactive insurance calculators help you determine:
      - How much coverage you need based on your lifestyle, assets, and future plans
      - Premium estimates for various types of insurance, including life, health, and motor insurance
      - Retirement and term life planning for long-term security
      These tools provide instant estimates, ensuring you can assess your options with precision and confidence before purchasing any insurance plan.`,
    },
    {
      icon: <Users className="w-6 h-6 text-indigo-600" />,
      title: "Trusted by Thousands",
      content: `FinMaster has earned the trust of thousands of users across India. Through our verified customer reviews, case studies, and ratings, we provide authentic insights into real customer experiences. Our platform's transparency in pricing, policy terms, and insurer reliability helps you make informed choices based on trusted, community-shared feedback.
      We also offer dedicated customer support to guide you through the entire insurance selection process, ensuring that you feel supported every step of the way. Whether you’re new to insurance or a seasoned investor, FinMaster is your trusted partner in navigating the complexities of the insurance world.`,
    },
  ];
  const ad = {
    image: "/assets/new1.png",
    link: "https://play.google.com/store/apps/details?id=finance.global.travel.niyo&pcampaignid=web_share",
    altText: "Advertisement",
  };
  return (
    <>
      {/* <Helmet>
        <title>What is Demat Account - Meaning, Features, Types, Benefits</title>
        <meta 
          name='description' 
          content='A demat account helps investors to hold their shares and securities in an electronic format. Know its meaning, types, features, benefits, and more on thefinmaster.'
        />
        <meta 
          name='keywords' 
          content='what is demat and trading account, what is demat account meaning' 
        />
        <link rel="canonical" href="https://thefinmaster.com/insurance_page" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet> */}
      <FAQSchema faqs={faqs} />
      <nav
        className="text-gray-500 text-sm my-2 ml-10 mt-10"
        aria-label="Breadcrumb"
      >
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Insurance Page</span>
          </li>
        </ol>
      </nav>

      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-4xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-0 pl-6">
                  <span className="text-blue-600 ">
                    Shield your family from life’s surprises
                  </span>{" "}
                  with robust insurance.
                </h1>

                {/* <a href="https://www.acko.com/two-wheeler-insurance/"
                 target="_blank" 
                 rel="noopener noreferrer">
                  <button className="py-2 px-4   border rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl text-lg mt-10 font-medium">
                    Open Account!
                  </button>
                </a> */}
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full"
                src="../assets/insure.jpeg"
                alt="Noimage"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="shadow-lg rounded-lg p-4 md:p-8 lg:p-12 mb-6 px-6 mx-4 md:mx-8 lg:mx-10 text-gray-600 flex justify-center items-center">
        <p className="text-lg md:text-base lg:text-lg text-center">
          In today's fast-paced environment, ease of use is paramount. Insurance
          offers a robust solution, merging simplicity with extensive coverage.
          Forget about potential uncertainties – a well-chosen policy ensures
          you’re protected against a range of risks. Beyond mere convenience,
          the benefits are substantial. At TheFinMaster.com, we’ll navigate you
          through the dynamic landscape of insurance, helping you secure the
          ideal protection for your needs.{" "}
        </p>
      </div>
      <div></div>

      <h1 className="text-center text-2xl md:text-2xl lg:text-5xl font-bold my-10">
        Featured Insurance Cards
      </h1>
      <div className=" flex items-center justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 px-4 py-8">
          {cardDataS.map((card, index) => (
            <motion.div
              key={index}
              className="flex flex-col justify-between relative rounded-lg overflow-hidden shadow-lg border border-gray-200 w-full h-full"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              {/* Type Badge */}
              <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-3 py-1 rounded-full shadow-md z-10">
                {card.type.toUpperCase()}
              </div>

              {/* Image */}
              <img
                className="w-full h-36 object-contain p-4"
                src={card.imageUrl}
                alt={card.title}
              />

              {/* Card Content */}
              <div className="flex-grow px-4 pb-2">
                <div className="font-bold text-lg mb-1 text-center text-blue-500">
                  {card.title}
                </div>
                {renderCardDetails(card)}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-3 py-3 px-4 mt-auto">
                <a
                  className="bg-blue-500 hover:bg-blue-700 text-white font-medium py-1 px-3 rounded-full text-sm transition duration-300"
                  href={card.learnMoreLink}
                  target="_blank"
                  rel="noreferrer"
                >
              See Details
                </a>
                <a
                  className="bg-green-500 hover:bg-green-700 text-white font-medium py-1 px-3 rounded-full text-sm transition duration-300"
                  href={card.applyNowLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Apply Now
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 ">
        {cardDataS.map((card, index) => (
          <motion.div 
            key={index} 
            className="rounded-lg overflow-hidden shadow-lg border border-gray-200 max-w-xs"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <img
              className="w-full"
              src={card.imageUrl}
              alt={card.title} 
            />
            <div className="px-3 py-2">
              <div className="font-bold text-lg mb-1 text-center text-blue-500">{card.title}</div>
              <p className="text-gray-600 text-base text-center mt-2"><strong>Joining Fee:</strong> {card.premiumStarting}</p>
              <p className="text-gray-600 text-base text-center mt-2"><strong>Rewards Rate:</strong> {card.claimSettlement}</p>
              <p className="text-gray-600 text-base text-center mt-2"><strong>Rewards Rate:</strong> {card.networkGarages}</p>

            </div>
            <div className="flex justify-center py-2">  
            <a      
                className="bg-blue-500 hover:bg-blue-700 text-white font-medium py-1 px-3 rounded-full mr-2 text-sm transition duration-300 ease-in-out"
                href={card.learnMoreLink}
                target= "blank"
                rel="nofollow"
              > 
                View Card
              </a>
              <a
                className="bg-green-500 hover:bg-green-700 text-white font-medium py-1 px-3 rounded-full text-sm transition duration-300 ease-in-out"
                href={card.applyNowLink}
                target= "blank"
                rel="nofollow"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        ))}
      </div> */}
      </div>
      <div className="w-full flex justify-center items-center my-2 px-4">
          <a
            href={ad.link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full max-w-5xl"
          >
            <img
              src={ad.image}
              alt={ad.altText}
              className="w-full h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105"
            />
          </a>
        </div>
      <div className="flex justify-center items-center  p-4 mt-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center "
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            What is Insurance?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Insurance is a financial product designed to protect individuals,
            families, businesses, and assets from unexpected losses or risks. At
            its core, insurance operates on a simple principle: risk pooling.
            You pay a premium to an insurance provider, and in return, they
            offer a safety net in the form of compensation if a predefined loss
            occurs. Whether it's a car accident, a medical emergency, a theft,
            or an untimely demise, insurance helps soften the financial blow.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            It’s not just a contract—it’s a financial buffer, a peace-of-mind
            tool, and often, a legal or social necessity. With rising
            uncertainties in life and increasing costs of living and healthcare,
            having the right insurance is more of a need than a choice.{" "}
          </p>
        </motion.div>
      </div>

      {/* <section className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="text-2xl md:text-3xl font-bold text-center text-blue-700 mb-6">
        📌 Important Types of Insurance
      </h1>
      <p className="text-center text-gray-600 mb-10">
      Insurance comes in a variety of forms, catering to different needs, stages of life, and asset types. While the insurance industry spans across dozens of niches, there are a few that are essential for every individual and family, either due to legal mandates, high risk, or financial prudence.
      </p>

      <div className="space-y-10">
        {loanData.map((loan, index) => (
          <div
            key={index}
            className="bg-white shadow-md border border-gray-200 rounded-xl p-6 hover:shadow-xl transition duration-300"
          >
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">{loan.title}</h2>
            {loan.content.split('\n').map((para, i) => (
              <p key={i} className="text-gray-700 mb-3 whitespace-pre-line">
                {para}
              </p>
            ))}
          </div>
        ))}
      </div>
    </section> */}
      <InsuranceHomeComponent />
      <section className="bg-gray-50 py-6 px-6 md:px-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
          What All Are Covered in Insurance?
        </h2>
        {/* <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
        Insurance coverage is designed to protect you from the unexpected—
        whether it's medical expenses, accidents, travel hiccups, or property
        damage. Here's a comprehensive list of what most insurance policies
        commonly cover:
      </p> */}

        <div className="grid gap-10 md:grid-cols-2">
          {insuranceCoverage.map((item, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition border-t-4 border-blue-500"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-indigo-100 rounded-full">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {item.title}
                </h3>
              </div>
              <p className="text-gray-700 text-base">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-6 px-6 md:px-16">
        <h2 className="text-2xl md:text-3xl  font-bold text-center text-gray-900 mb-10">
          📊 Important Insurance Statistics in India
        </h2>

        <div className="space-y-10">
          {/* 1. Health Insurance Coverage */}
          <div className="bg-gray-50 p-6 rounded-xl shadow-md border-l-4 border-indigo-500">
            <div className="flex items-start gap-4 mb-3">
              <div className="text-3xl text-red-600">
                <FaUserMd />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Health Insurance Coverage
              </h3>
            </div>
            <p className="text-gray-700">
              As of the latest available data, approximately 30% of India's
              population is covered under some form of health insurance. This
              includes both public schemes like Ayushman Bharat and private
              health insurance policies. The Insurance Regulatory and
              Development Authority of India (IRDAI) has been actively working
              to increase this coverage through initiatives like 'Insurance for
              All by 2047' and the introduction of digital platforms such as
              'Bima Sugam' to enhance accessibility and transparency in the
              insurance sector.
            </p>
          </div>

          {/* 2. Insurance Penetration */}
          <div className="bg-gray-50 p-6 rounded-xl shadow-md border-l-4 border-blue-500">
            <div className="flex items-start gap-4 mb-3">
              <div className="text-3xl text-blue-600">
                <FaBalanceScale />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Insurance Penetration
              </h3>
            </div>
            <p className="text-gray-700">
              India's overall insurance penetration, defined as the ratio of
              insurance premiums to Gross Domestic Product (GDP), stood at 3.7%
              in the fiscal year 2023-24, a slight decline from 4% in the
              previous year. This is significantly below the global average of
              7%, indicating substantial room for growth in the Indian insurance
              market.
            </p>
            <ul className="mt-3 list-disc list-inside text-gray-700 space-y-1">
              <li>
                <strong>Life Insurance Penetration:</strong> Decreased from 3%
                to 2.8% in 2023-24.
              </li>
              <li>
                <strong>Non-Life Insurance Penetration:</strong> Remained steady
                at 1% during the same period.
              </li>
            </ul>
            <p className="text-gray-700 mt-2">
              Despite the decline in penetration, the total insurance premium
              collected in India grew by 7.7% in FY24, reaching ₹11.2 lakh
              crore.
            </p>
          </div>

          {/* 3. Growth of the General Insurance Industry */}
          <div className="bg-gray-50 p-6 rounded-xl shadow-md border-l-4 border-green-600">
            <div className="flex items-start gap-4 mb-3">
              <div className="text-3xl text-green-600">
                <FaChartLine />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Growth of the General Insurance Industry
              </h3>
            </div>
            <p className="text-gray-700">
              India's general insurance sector is projected to grow from $40.36
              billion in 2024 to $57.3 billion by 2028, representing a Compound
              Annual Growth Rate (CAGR) of 9.9%.
            </p>
            <ul className="mt-3 list-disc list-inside text-gray-700 space-y-1">
              <li>
                <strong>Personal Accident and Health (PA&H) Insurance:</strong>{" "}
                Expected to be the largest segment, accounting for 39.5% of the
                general insurance market in 2024, with a projected CAGR of 12.5%
                from 2024 to 2028.
              </li>
              <li>
                <strong>Motor Insurance:</strong> Anticipated to grow at a CAGR
                of 7.9% during the same period, driven by increasing vehicle
                sales and heightened awareness of insurance benefits.
              </li>
              <li>
                <strong>Property Insurance:</strong> Projected to grow at a CAGR
                of 8.3%, supported by increased investments in infrastructure
                and real estate.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="bg-white text-gray-900  py-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">
          🛡️ Factors to Consider Before Choosing Insurance
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-4">
          Choosing the right insurance policy is not just about picking the
          cheapest option. It’s a long-term financial decision that impacts your
          safety, savings, and peace of mind. Below are 8 crucial factors you
          must evaluate thoroughly before making a decision:
        </p>
        <div className="max-w-7xl mx-auto space-y-4 px-2">
          {factors.map((item, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl shadow-sm"
            >
              <button
                className="flex items-center justify-between w-full p-5 text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span className="text-lg font-medium">{item.title}</span>
                </div>
                <span className="text-xl text-gray-500">
                  {openIndex === index ? "−" : "+"}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-5 pb-5 text-gray-700 whitespace-pre-line"
                  >
                    {item.content}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
      <section className="bg-white py-6 px-4 md:px-16">
        <h2 className="text-2xl md:text-3xl  font-bold text-gray-900 mb-4 text-center">
          Factors Affecting Insurance Premiums and Policy Terms
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-4">
          Insurance premiums and policy terms are not uniform—they’re
          dynamically calculated based on a variety of risk-related factors.
          Here’s an expanded explanation of the top elements insurers consider
          before offering a quote or setting terms:
        </p>
        <div className="space-y-8">
          {factors2.map((item, index) => (
            <div
              key={index}
              className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition duration-300"
            >
              <h3 className="text-xl font-semibold text-blue-600 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-700 whitespace-pre-line text-base">
                {item.content}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-6 px-4 md:px-16">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            🛡️ How FinMaster Helps You Choose the Right Insurance
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
          Finding the right insurance policy can be confusing, with dozens of terms, hidden clauses, and overwhelming options. That’s where FinMaster comes in. We simplify your decision-making process with unbiased, research-driven support tailored to your needs.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {insurancePoints.map((item, index) => (
            <div
              key={index}
              className="bg-gray-50 p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                {item.icon}
                <h3 className="text-xl font-semibold text-gray-800">
                  {item.title}
                </h3>
              </div>
              <p className="text-gray-700 whitespace-pre-line">
                {item.content}
              </p>
            </div>
          ))}
        </div>
      </section>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6  px-2">
        <h1 className="text-center text-2xl md:text-3xl  font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default InsuranceNewUiHome;
